import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
} from "@chakra-ui/react";

import { useDeleteBankAccount } from "api/payments/index.js";

/**
 * RemoveBankAccount - Component to conditionally display a modal that allows the ability to remove a bank account.
 * @param {RemoveBankAccountProps} props
 * @param {Boolean} props.isOpen - Dictates whether modal is open or closed.
 * @param {Function} props.onClose - function to close modal.
 * @param {Object} props.bankAccount - The bank account that this component is responsible for.
 * @param {Function} props.onComplete - callback function to handle success case.
 * @returns JSX.Element
 */
const RemoveBankAccountModal = ({ isOpen, onClose, bankAccount, onComplete }) => {
  const { formatMessage } = useIntl();
  const {
    mutateAsync: deleteBankAccount,
    isSuccess: isDeletingBASuccess,
    isLoading: isDeletingBA,
    error: deleteBAError,
  } = useDeleteBankAccount();

  const toast = useToast();

  const handleSubmit = async () => {
    deleteBankAccount({ bankAccountId: bankAccount.bank_account_id });
  };

  useEffect(() => {
    if (isDeletingBASuccess) {
      toast({
        title: formatMessage({
          defaultMessage: "Success",
          description: "Popup title",
        }),
        description: formatMessage({
          defaultMessage: "Bank account successfully removed",
          description: "Popup description",
        }),
        status: "success",
        isClosable: true,
      });
      onComplete();
      onClose();
    }
  }, [isDeletingBASuccess, onComplete, toast, onClose, formatMessage]);

  useEffect(() => {
    if (deleteBAError) {
      toast({
        title: formatMessage({
          defaultMessage: "Error",
          description: "Popup title",
        }),
        description: deleteBAError.message,
        status: "error",
        isClosable: true,
      });
    }
  }, [deleteBAError, toast, formatMessage]);
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <FormattedMessage defaultMessage="Remove Bank Account" description="Modal header" />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormattedMessage
            defaultMessage="Are you sure you want to remove this bank account?"
            description="Modal body text"
          />
        </ModalBody>
        <ModalFooter justifyContent="flex-end">
          <Button onClick={handleSubmit} isDisabled={isDeletingBA}>
            <FormattedMessage defaultMessage="Submit" description="Button label" />
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RemoveBankAccountModal;
